import { Response as ApplicationResponse } from 'api/typegen/client/application/get';
import { datadogLogs } from '@datadog/browser-logs';
import {
  isSteloResolutionDetail,
  isTinkRejectedByMessage,
  isUserRejectedByDetail,
} from 'utils/constants/constants_tinkStelo';
import { logout } from 'actions/logout';
import { ROUTES } from 'utils/constants/constants_routes';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavigateOrigin } from 'hooks/useNavigateOrigin';
import IdentifyEvent from 'hooks/eventCollector/events/IdentifyEvent';
import useApplication from 'hooks/useApplication';
import useEventCollector from 'hooks/eventCollector/useEventCollector';
import useTestAB from './useTestAB';

export const useApplicationResolution = () => {
  const { identify } = useEventCollector();
  const { navigateOrigin } = useNavigateOrigin();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { data: application, fetch: fetchApplication } = useApplication();
  const steloRedirectionEnabled =
    pathname === ROUTES.FIRST_LOAN_ONLINE_BANK_CHECK;
  const canRedirectToStelo = (variant: string) =>
    variant === 'variantA' && steloRedirectionEnabled;
  const { variant: variantSteloApproved } = useTestAB({
    testName: 'steloApproved',
    customPercentVariantA: 42,
  }); // 40% users will see stelo - approv
  const { variant: variantSteloRejectedByTink } = useTestAB({
    testName: 'steloRejectedByTink',
    customPercentVariantA: 20,
  }); // 20% users will see stelo - rejected by tink
  const { variant: variantSteloRejected } = useTestAB({
    testName: 'steloRejected',
    customPercentVariantA: 15,
  }); // 15% users will see stelo - rejected

  const navigateByApplicationResolution = async () => {
    const { resolution, resolutionDetail, resolutionMessage } =
      application || ((await fetchApplication()) as ApplicationResponse);

    if (application) {
      identify(
        new IdentifyEvent({
          application,
        }),
      );
    }

    // Before change this switch. Please, check this -> https://wiki.4finance.net/display/FD/Flow+outcome+mapping
    switch (resolution) {
      case 'APPROVED':
        if (canRedirectToStelo(variantSteloApproved))
          return navigate(ROUTES.STELO_APPROVED);
        return navigate(ROUTES.FIRST_LOAN_ONLINE_BANK_APPROVED);

      case 'MANUAL':
        return navigate(ROUTES.FIRST_LOAN_ONLINE_BANK_ADD_DOCUMENTS);

      case 'REJECTED':
        // To reproduce this error: tink extension -> APPROVED + APPROVED + 631
        if (resolutionDetail === 'NEW_OFFER') {
          return navigate(ROUTES.BEST_OFFER);
        }
        if (isTinkRejectedByMessage(resolutionMessage)) {
          if (canRedirectToStelo(variantSteloRejectedByTink))
            return navigate(ROUTES.STELO_REJECTED_TINK);
          return navigate(ROUTES.FIRST_LOAN_ONLINE_BANK_REJECTED_BY_TINK);
        }
        if (isUserRejectedByDetail(resolutionDetail)) {
          await logout();
          return navigateOrigin();
        }

        if (
          isSteloResolutionDetail(resolutionDetail) &&
          canRedirectToStelo(variantSteloRejected)
        ) {
          return navigate(ROUTES.STELO_REJECTED);
        }
        return navigate(ROUTES.FIRST_LOAN_REJECTED);
      case 'CANCELLED':
        if (resolutionDetail === 'EXPIRED')
          return navigate(ROUTES.FIRST_LOAN_WELCOME);
        return navigate(ROUTES.FIRST_LOAN_REJECTED);
      default:
        // Note: Invalid state, user sees spinner indefinetly
        datadogLogs.logger.error('User ended up in invalid state.', {
          application,
        });
        return;
    }
  };

  return { navigateByApplicationResolution };
};
